import React, { useEffect, useRef, useState } from 'react';
import API_BASE from '../../utils/apiBase';
import {
  BODY_VIEWS, VIEW_LABELS, VIEW_BANDS,
  clampPct, normalizeView, regionDefaultView, regionSuggestedCoords,
} from './bodyMapGeometry';

// Natural template dimensions (used for the SVG crop viewBox). Refined on load.
const DEFAULT_DIMS = { male: { w: 524, h: 826 }, female: { w: 1125, h: 1788 } };

/**
 * BodyDiagramPicker — enlarged single-view tap-to-place on the REAL CQC Expert
 * template. The active view's figure is cropped from the full composite via an
 * SVG viewBox and shown large. A tap is converted to FULL-template percentages
 * (the single stored coordinate system) so the exported PDF, which renders the
 * complete original template, places the marker at the identical point.
 */
export default function BodyDiagramPicker({ gender, region, view, xPct, yPct, marks = [], onChange }) {
  const svgRef = useRef(null);
  const g = String(gender || 'male').trim().toLowerCase() === 'female' ? 'female' : 'male';
  const templateSrc = `${API_BASE}/body-map-template/${g}`;
  const activeView = normalizeView(view) || regionDefaultView(region);
  const [y0, y1] = VIEW_BANDS[activeView] || [0, 100];

  const [dims, setDims] = useState(DEFAULT_DIMS[g]);
  useEffect(() => {
    setDims(DEFAULT_DIMS[g]);
    const im = new Image();
    im.onload = () => setDims({ w: im.naturalWidth, h: im.naturalHeight });
    im.src = templateSrc;
  }, [templateSrc, g]);

  const IMG_W = dims.w, IMG_H = dims.h;
  const vy0 = (y0 / 100) * IMG_H;
  const vh = ((y1 - y0) / 100) * IMG_H;

  const switchView = (v) => onChange({ view: v, xPct: null, yPct: null });

  const placeFromEvent = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const cx = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const cy = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (cx == null || cy == null || rect.width === 0 || rect.height === 0) return;
    const fracX = Math.min(1, Math.max(0, (cx - rect.left) / rect.width));
    const fracY = Math.min(1, Math.max(0, (cy - rect.top) / rect.height));
    // Convert crop-fraction -> FULL-template percentages (single stored system).
    const fullX = clampPct(fracX * 100);
    const fullY = clampPct(y0 + fracY * (y1 - y0));
    onChange({ view: activeView, xPct: Math.round(fullX * 10) / 10, yPct: Math.round(fullY * 10) / 10 });
  };

  // Full-template % -> position (%) inside the current crop, or null if outside.
  const toCrop = (fx, fy) => {
    if (fx == null || fy == null || fy < y0 - 0.01 || fy > y1 + 0.01 || (y1 - y0) === 0) return null;
    return { left: fx, top: ((fy - y0) / (y1 - y0)) * 100 };
  };

  const draft = toCrop(xPct, yPct);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 justify-center">
        {BODY_VIEWS.map((v) => (
          <button
            key={v} type="button" onClick={() => switchView(v)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              activeView === v ? 'bg-teal-700 text-white border-teal-700'
                               : 'bg-white text-slate-600 border-slate-300 hover:border-teal-400'}`}
            data-testid={`bodymap-view-${v}`}
          >
            {VIEW_LABELS[v]}
          </button>
        ))}
      </div>

      <div
        className="relative mx-auto bg-white border border-slate-200 rounded-xl overflow-hidden"
        style={{ width: '100%', maxWidth: '420px' }}
      >
        <svg
          ref={svgRef}
          viewBox={`0 ${vy0} ${IMG_W} ${vh}`}
          preserveAspectRatio="none"
          onPointerDown={placeFromEvent}
          className="block w-full select-none cursor-crosshair"
          style={{ aspectRatio: `${IMG_W} / ${vh}`, touchAction: 'pan-y' }}
          data-testid="bodymap-template-img"
          data-view={activeView}
          role="img"
          aria-label={`${VIEW_LABELS[activeView]} view of the CQC body map. Tap the exact location.`}
        >
          <image href={templateSrc} x="0" y="0" width={IMG_W} height={IMG_H} preserveAspectRatio="none" />
        </svg>

        {/* Already-added marks that fall in this view, numbered */}
        {marks.map((m, i) => {
          const p = toCrop(m.x_pct, m.y_pct);
          const mv = normalizeView(m.view) || regionDefaultView(m.region);
          if (!p || mv !== activeView) return null;
          return (
            <div key={`mark-${i}`}
              className="absolute flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold border-2 border-white shadow pointer-events-none"
              style={{ left: `${p.left}%`, top: `${p.top}%`, width: '22px', height: '22px', transform: 'translate(-50%, -50%)' }}
              data-testid={`bodymap-mark-${i + 1}`}
            >{i + 1}</div>
          );
        })}

        {/* In-progress (draft) marker */}
        {draft && (
          <div
            className="absolute rounded-full bg-red-500 ring-2 ring-white shadow pointer-events-none animate-pulse"
            style={{ left: `${draft.left}%`, top: `${draft.top}%`, width: '20px', height: '20px', transform: 'translate(-50%, -50%)' }}
            data-testid="bodymap-marker"
          />
        )}
      </div>
      <p className="text-xs text-slate-500 text-center">
        Tap the exact location on the {VIEW_LABELS[activeView].toLowerCase()} view above.
      </p>
    </div>
  );
}
